import { baseApi } from "../api/baseApi";
import { toast } from "sonner";
import {
  CustomerCommunication,
  ContactPersons,
  NewCompanyCommunication,
  DeleteCustomerCommunication,
} from "../types/communication.types";

export const communicationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Customer communication endpoints (from customerCommunicationApiRTK.ts)
    getUsersCommunications: builder.query<CustomerCommunication[], number>({
      query: (customerNo) => `/v1/crm/customer/${customerNo}/communications`,
      keepUnusedDataFor: 30,
      providesTags: (result, error, customerNo) =>
        result
          ? [
              { type: "Communications", customerNo },
              { type: "Communications", id: "LIST" },
            ]
          : [{ type: "Communications", id: "LIST" }],
    }),

    postUsersCommunications: builder.mutation<
      CustomerCommunication,
      {
        customerCommunication: Partial<CustomerCommunication>[];
        customerNo: number;
      }
    >({
      query: ({ customerCommunication, customerNo }) => ({
        url: `/v1/crm/customer/${customerNo}/updatecommunications`,
        method: "POST",
        body: customerCommunication,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Kontakt uppdaterad.");
        } catch {
          toast.error("Det gick inte uppdatera kontakten, försök igen senare.");
        }
      },
      invalidatesTags: (result, error, { customerNo }) => [
        { type: "Communications", customerNo },
        "ContactPersons",
      ],
    }),

    deleteUsersCommunications: builder.mutation<
      CustomerCommunication,
      {
        customerCommunication: Partial<CustomerCommunication>[];
        customerNo: number;
      }
    >({
      query: ({ customerCommunication, customerNo }) => ({
        url: `/v1/crm/customer/${customerNo}/deletecommunications`,
        method: "POST",
        body: customerCommunication,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Kontakt raderad.");
        } catch {
          toast.error("Det gick inte radera kontakten, försök igen senare.");
        }
      },
      invalidatesTags: (result, error, { customerNo }) => [
        { type: "Communications", customerNo },
        "ContactPersons",
      ],
    }),

    // Company communication endpoints (from customerCompanyCommunicationApiRTK.ts)
    getContactPerson: builder.query<ContactPersons[], number>({
      query: (customerNo) => `/v2/crm/customer/${customerNo}/contact-persons`,
      providesTags: ["ContactPersons"],
    }),

    postNewCompanyCommunication: builder.mutation<
      NewCompanyCommunication,
      {
        data: NewCompanyCommunication;
        customerId: number;
      }
    >({
      query: ({ data, customerId }) => ({
        url: `/v2/crm/customer/${customerId}/contact-persons`,
        method: "POST",
        keepUnusedDataFor: 30,
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Kontakt uppdaterad.");
        } catch {
          toast.error("Det gick inte uppdatera kontakten, försök igen senare.");
        }
      },
      invalidatesTags: (result, error, { customerId }) => [
        { type: "Communications", customerId },
        "ContactPersons",
      ],
    }),

    deleteCompanyCommunication: builder.mutation<
      DeleteCustomerCommunication,
      {
        customerId: number;
        contactPersonId: number;
      }
    >({
      query: ({ customerId, contactPersonId }) => ({
        url: `/v2/crm/customer/${customerId}/contact-persons/${contactPersonId}`,
        method: "DELETE",
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Kontakt raderad.");
        } catch {
          toast.error("Det gick inte radera kontakten, försök igen senare.");
        }
      },
      invalidatesTags: (result, error, { customerId }) => [
        { type: "Communications", customerId },
        "ContactPersons",
      ],
    }),
  }),
});

// Export hooks
export const {
  // Customer communications
  useGetUsersCommunicationsQuery,
  usePostUsersCommunicationsMutation,
  useDeleteUsersCommunicationsMutation,
  
  // Company communications
  useGetContactPersonQuery,
  usePostNewCompanyCommunicationMutation,
  useDeleteCompanyCommunicationMutation,
} = communicationApi;

// Re-export types for backward compatibility
export type {
  CustomerCommunication,
  ContactPersons,
  NewCompanyCommunication,
  DeleteCustomerCommunication
} from "../types/communication.types";
export {
  CommunicationTypeEnum,
  ContactPersonRolesEnum,
  ContactPersonRolesEnumLabel
} from "../types/communication.types";